import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import WinbackAnalyticsTile from '../components/admin/WinbackAnalyticsTile';
import RetargetingEventsTile from '../components/admin/RetargetingEventsTile';
import { 
  UserCheck, 
  UserX, 
  Clock, 
  CheckCircle, 
  XCircle,
  Shield,
  Users,
  AlertTriangle,
  Mail,
  Building2,
  Calendar,
  Key,
  Eye,
  EyeOff,
  RefreshCw,
  History,
  Sparkles,
  Image as ImageIcon,
  UserCircle,
  Trash2,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Full system access' },
  { value: 'sales_rep', label: 'Sales Representative', description: 'Standard sales access' },
  { value: 'executive_viewer', label: 'Executive Viewer', description: 'View-only access' },
  { value: 'creative_lead', label: 'Creative Lead', description: 'Design and creative access' },
];

const STATUS_CONFIG = {
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
  approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
};

export default function UserAccessManagement() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('sales_rep');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Password reset state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  // Feb 2026 — Custom Payment Link generator (admin-only). Additive: this
  // section does NOT touch any existing Stripe/subscription/billing-gate
  // flow. It creates a one-off Stripe Checkout Session for an arbitrary
  // CAD amount and returns the hosted URL for the admin to send.
  const [payLinkOpen, setPayLinkOpen] = useState(false);
  const [payLinkForm, setPayLinkForm] = useState({
    customer_email: "",
    tier: "starter",
    amount_cad: "",
    duration: "monthly",
  });
  const [payLinkGenerating, setPayLinkGenerating] = useState(false);
  const [payLinkResult, setPayLinkResult] = useState(null);
  const [payLinkError, setPayLinkError] = useState("");
  const [payLinkCopied, setPayLinkCopied] = useState(false);

  const handleGeneratePayLink = async () => {
    setPayLinkError("");
    setPayLinkResult(null);
    setPayLinkCopied(false);
    const body = {
      customer_email: payLinkForm.customer_email.trim(),
      tier: payLinkForm.tier,
      amount_cad: Number(payLinkForm.amount_cad),
      duration: payLinkForm.duration,
    };
    if (!body.customer_email || !body.customer_email.includes("@")) {
      setPayLinkError("Customer email is required.");
      return;
    }
    if (!(body.amount_cad > 0)) {
      setPayLinkError("Amount (CAD) must be greater than 0.");
      return;
    }
    setPayLinkGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/generate-payment-link`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setPayLinkResult(data);
        toast.success("Payment link generated");
      } else {
        setPayLinkError(data?.detail || "Failed to generate link");
        toast.error(data?.detail || "Failed to generate link");
      }
    } catch (e) {
      setPayLinkError(e.message || "Failed to generate link");
      toast.error(e.message || "Failed to generate link");
    } finally {
      setPayLinkGenerating(false);
    }
  };

  const handleCopyPayLink = async () => {
    if (!payLinkResult?.url) return;
    try {
      await navigator.clipboard.writeText(payLinkResult.url);
      setPayLinkCopied(true);
      toast.success("Link copied");
      setTimeout(() => setPayLinkCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  const [resetLogs, setResetLogs] = useState([]);
  const [showLogsDialog, setShowLogsDialog] = useState(false);

  // Comp grant dialog state (May 11, 2026)
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [compTargetUser, setCompTargetUser] = useState(null);
  const [compDays, setCompDays] = useState('30');
  const [compReason, setCompReason] = useState('One-click comp from User Access Management');
  const [compSubmitting, setCompSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle URL action parameters (from email links)
  useEffect(() => {
    const action = searchParams.get('action');
    const userId = searchParams.get('user_id');
    
    if (action && userId && users.length > 0) {
      const targetUser = users.find(u => u.id === userId);
      if (targetUser) {
        setSelectedUser(targetUser);
        if (action === 'approve') {
          setShowApproveDialog(true);
        } else if (action === 'reject') {
          setShowRejectDialog(true);
        }
      }
    }
  }, [searchParams, users]);

  const fetchUsers = async () => {
    try {
      if (!token) {
        toast.error('Authentication required');
        setLoading(false);
        return;
      }

      const [allUsersRes, pendingRes] = await Promise.all([
        fetch(`${API}/api/admin/all-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/admin/pending-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      let hasError = false;

      if (allUsersRes.ok) {
        const data = await allUsersRes.json();
        setUsers(data.users || []);
      } else {
        console.error('Failed to fetch all users:', allUsersRes.status);
        hasError = true;
      }

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingUsers(data.pending_users || []);
      } else {
        console.error('Failed to fetch pending users:', pendingRes.status);
        hasError = true;
      }

      if (hasError) {
        toast.error('Failed to load some user data');
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedUser || !selectedRole) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API}/api/admin/approve-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          action: 'approve',
          assigned_role: selectedRole
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'User approved successfully');
        setShowApproveDialog(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to approve user');
      }
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedUser) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API}/api/admin/approve-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          action: 'reject'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'User rejected');
        setShowRejectDialog(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to reject user');
      }
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setProcessing(false);
    }
  };

  const DEFAULT_COMP_REASON = 'One-click comp from User Access Management';

  const handleCompDaysChange = (newValue) => {
    // When admin picks a non-default duration (7 / 14 / 60 / 90), clear the
    // boilerplate reason so they're nudged to type a real one for the audit
    // log. Switching back to 30 restores the default if they hadn't typed
    // anything custom.
    setCompDays(newValue);
    if (newValue !== '30' && compReason === DEFAULT_COMP_REASON) {
      setCompReason('');
    } else if (newValue === '30' && compReason === '') {
      setCompReason(DEFAULT_COMP_REASON);
    }
  };

  const handleCompUser = (targetUser) => {
    // Opens the dialog so admin can pick duration (default 30d). The actual
    // API call happens in `submitCompGrant` after Confirm is clicked.
    if (!targetUser) return;
    setCompTargetUser(targetUser);
    setCompDays('30');
    setCompReason('One-click comp from User Access Management');
    setShowCompDialog(true);
  };

  const submitCompGrant = async () => {
    if (!compTargetUser) return;
    setCompSubmitting(true);
    try {
      const response = await fetch(`${API}/api/billing/admin/comp-grant/user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: compTargetUser.id,
          tier: 'professional',
          duration_days: parseInt(compDays, 10),
          reason: (compReason || '').trim() || null,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(
          `Approved ${compTargetUser.email} · ${compDays}-day comp ready (user must enter card via /pricing → /signup to activate)`
        );
        setShowCompDialog(false);
        setCompTargetUser(null);
        fetchUsers();
      } else {
        const detail = data?.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to comp user');
        toast.error(msg);
      }
    } catch (error) {
      toast.error('Failed to comp user');
    } finally {
      setCompSubmitting(false);
    }
  };

  // One-shot backfill: walks every Vision Deck and rehosts any external
  // image URLs (Google avatars, AI-generator CDN URLs, etc.) to permanent
  // /api/media/ paths. Idempotent — safe to re-run.
  const handleBackfillDeckImages = async () => {
    if (!window.confirm(
      'Walk every Vision Deck and rehost any external image URLs (Google ' +
      'avatars, AI-generated images, pasted CDN links) to permanent local ' +
      'storage?\n\nIdempotent — safe to re-run anytime.'
    )) return;
    try {
      const response = await fetch(`${API}/api/admin/backfill-deck-images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        const errCount = (data.errors || []).length;
        toast.success(
          `Scanned ${data.scanned} decks, updated ${data.updated}, ` +
          `rehosted ${data.rehosted_images} image${data.rehosted_images === 1 ? '' : 's'}` +
          (errCount ? ` · ${errCount} error${errCount === 1 ? '' : 's'}` : '')
        );
      } else {
        toast.error(data?.detail || 'Image backfill failed');
      }
    } catch (e) {
      toast.error('Image backfill failed');
    }
  };

  // One-shot migration: walks every user with a signature_headshot_url
  // pointing at the legacy ephemeral disk (`/api/uploads/...`) and copies
  // the bytes into GridFS so the photo survives container restarts and
  // production redeploys. Idempotent — files already in GridFS are skipped.
  // One-shot cleanup — remove `market_signals` rows whose company_name
  // starts with `TEST_` (leftover QA noise). Idempotent; safe to click
  // repeatedly. Inline result string reflects the server-side count.
  const [purgeResult, setPurgeResult] = useState('');
  const handlePurgeTestSignals = async () => {
    setPurgeResult('');
    try {
      const res = await fetch(`${API}/api/admin/purge-test-signals`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setPurgeResult(`Deleted ${data.deleted} stale test signals`);
        toast.success(`Deleted ${data.deleted} stale test signals`);
      } else {
        setPurgeResult('Purge failed — try again');
        toast.error(data?.detail || 'Purge failed — try again');
      }
    } catch {
      setPurgeResult('Purge failed — try again');
      toast.error('Purge failed — try again');
    }
  };

  // Purge stale job / hiring / recruiter / LinkedIn signals for the calling
  // admin. Aligns with the disallowed-intent filter in
  // sweep_query_builder._query_has_disallowed_intent(). Idempotent.
  // Feb 2026 (Task 1) — Purge every market_signal for a supplied user_id.
  // Prompts the admin so they can target their own account or another
  // tenant's. Additive: does not replace or remove any existing purge
  // handler. Idempotent — repeat calls return {deleted:0}.
  const handlePurgeMySignals = async () => {
    const targetUid = window.prompt(
      'Enter the user_id whose market_signals should be DELETED (tip: use your own admin id to reset your radar):',
      ''
    );
    if (!targetUid || !targetUid.trim()) return;
    if (!window.confirm(
      `Delete ALL market_signals for user_id "${targetUid.trim()}"?\n\n` +
      `This is irreversible. Signals for other users are untouched.`
    )) return;
    setPurgeResult('');
    try {
      const res = await fetch(
        `${API}/api/admin/purge-user-signals?user_id=${encodeURIComponent(targetUid.trim())}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      if (res.ok) {
        setPurgeResult(`Deleted ${data.deleted} signals for user`);
        toast.success(`Deleted ${data.deleted} signals for user`);
      } else {
        setPurgeResult('Purge failed — try again');
        toast.error(data?.detail || 'Purge failed — try again');
      }
    } catch {
      setPurgeResult('Purge failed — try again');
      toast.error('Purge failed — try again');
    }
  };

  const handlePurgeJobHiringSignals = async () => {
    if (!window.confirm(
      'Delete every market signal in your radar whose title, description, ' +
      'or source references a job posting, hiring notice, recruiter, or ' +
      'LinkedIn profile page?\n\nThese are leftover signals from before ' +
      'the disallowed-intent filter was added. Safe to re-run anytime.'
    )) return;
    setPurgeResult('');
    try {
      const res = await fetch(`${API}/api/admin/purge-job-hiring-signals`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setPurgeResult(`Deleted ${data.deleted} stale job/hiring signals`);
        toast.success(`Deleted ${data.deleted} stale job/hiring signals`);
      } else {
        setPurgeResult('Purge failed — try again');
        toast.error(data?.detail || 'Purge failed — try again');
      }
    } catch {
      setPurgeResult('Purge failed — try again');
      toast.error('Purge failed — try again');
    }
  };

  const handleMigrateHeadshots = async () => {
    if (!window.confirm(
      'Move every signature headshot from the ephemeral upload volume into ' +
      'durable GridFS storage and rewrite each user\'s headshot URL to ' +
      '/api/media/...?\n\nIdempotent — safe to re-run anytime.'
    )) return;
    try {
      const response = await fetch(`${API}/api/admin/migrate-signature-headshots`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        const errCount = (data.errors || []).length;
        toast.success(
          `Scanned ${data.scanned} user${data.scanned === 1 ? '' : 's'} · ` +
          `migrated ${data.migrated} to GridFS · ` +
          `already in GridFS ${data.already_in_gridfs} · ` +
          `URLs rewritten ${data.url_rewritten}` +
          (data.missing_on_disk ? ` · missing on disk ${data.missing_on_disk}` : '') +
          (errCount ? ` · ${errCount} error${errCount === 1 ? '' : 's'}` : '')
        );
      } else {
        toast.error(data?.detail || 'Headshot migration failed');
      }
    } catch (e) {
      toast.error('Headshot migration failed');
    }
  };

  // ── OAuth billing-gate admin actions (additive, post Feb-2026 deploy) ──

  // One-shot retroactive flag: marks already-approved Google-OAuth users
  // who never linked a Stripe customer with billing_status="pending_billing".
  // Safe to re-run — idempotent. After running, use "Re-send billing setup
  // link" per-user to email them the magic link.
  const handleBackfillOauthBilling = async () => {
    if (!window.confirm('Flag every approved user (OAuth or email/password) who has no Stripe customer with billing_status=pending_billing?\n\nMaster admin is exempt. They will lose API access until they finish billing setup. You can then re-send the setup email per user.')) {
      return;
    }
    try {
      const response = await fetch(`${API}/api/admin/backfill-all-billing-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        const byMethod = data.by_signup_method || {};
        toast.success(
          `Flagged ${data.modified_count} user${data.modified_count === 1 ? '' : 's'} for billing setup ` +
          `(${byMethod.password || 0} email/password, ${byMethod.oauth || 0} OAuth)`
        );
        fetchUsers();
      } else {
        toast.error(data?.detail || 'Backfill failed');
      }
    } catch (e) {
      toast.error('Backfill failed');
    }
  };

  // Per-user resend: mints a fresh 7-day token + re-sends the approval email
  // (with the billing-setup magic link). Only meaningful for OAuth users
  // stuck in pending_billing.
  const handleResendBillingSetup = async (targetUser) => {
    if (!targetUser?.id) return;
    try {
      const response = await fetch(`${API}/api/admin/users/${targetUser.id}/resend-billing-setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || `Setup link re-sent to ${targetUser.email}`);
      } else {
        const detail = data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Resend failed');
      }
    } catch (e) {
      toast.error('Resend failed');
    }
  };

  // Fail-safe override: force-activate a user without Stripe.
  // Grants a permanent access_override + flips billing_status to active.
  // Master admin only on the backend — gracefully reports 403 if anyone else tries.
  const handleForceActivate = async (targetUser) => {
    if (!targetUser?.id) return;
    if (!window.confirm(
      `Grant ${targetUser.email} full access WITHOUT Stripe payment?\n\n` +
      `This flips their billing_status to "active" and adds a permanent ` +
      `access override. They will be able to log in immediately and skip ` +
      `the credit-card requirement.\n\n` +
      `Reversible: you can revoke the override from this page anytime.`
    )) return;
    try {
      const response = await fetch(`${API}/api/admin/users/${targetUser.id}/force-activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || `${targetUser.email} is now active.`);
        fetchUsers();
      } else {
        const detail = data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Force-activate failed');
      }
    } catch (e) {
      toast.error('Force-activate failed');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/api/admin/update-user-role/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('User role updated');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  // Password Reset Functions
  const openPasswordResetDialog = (targetUser) => {
    setPasswordResetUser(targetUser);
    setNewPassword('');
    setShowPassword(false);
    setShowPasswordDialog(true);
  };

  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };

  const handleResetPassword = async () => {
    if (!passwordResetUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const response = await fetch(`${API}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: passwordResetUser.id,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Password reset for ${passwordResetUser.email}`);
        setShowPasswordDialog(false);
        setPasswordResetUser(null);
        setNewPassword('');
        fetchResetLogs();
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Error resetting password');
      console.error(error);
    } finally {
      setResettingPassword(false);
    }
  };

  const fetchResetLogs = async () => {
    try {
      const res = await fetch(`${API}/api/admin/password-reset-logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setResetLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching reset logs:', error);
    }
  };

  useEffect(() => {
    if (token) fetchResetLogs();
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="user-access-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
              <Shield className="w-7 h-7 text-[#B89B5E]" />
              User Access Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Review and manage user access requests
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* All admin-only maintenance actions collapsed into a single
                dropdown so the header stays clean as we add more of them.
                Each item behaves identically to the previous standalone
                buttons — additive UX refactor only. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="admin-actions-menu-trigger"
                >
                  <Wrench className="h-4 w-4" />
                  Admin Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Maintenance</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={handleBackfillOauthBilling}
                  data-testid="backfill-oauth-billing-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Flag unbilled users
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleBackfillDeckImages}
                  data-testid="backfill-deck-images-btn"
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Rehost deck images
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleMigrateHeadshots}
                  data-testid="migrate-signature-headshots-btn"
                >
                  <UserCircle className="h-4 w-4 mr-2" />
                  Migrate headshots to GridFS
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handlePurgeTestSignals}
                  data-testid="purge-test-signals-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Purge Test Signals
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handlePurgeJobHiringSignals}
                  data-testid="purge-job-hiring-signals-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Purge job/hiring signals
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handlePurgeMySignals}
                  data-testid="purge-user-signals-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Purge My Signals
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setShowLogsDialog(true)}>
                  <History className="h-4 w-4 mr-2" />
                  Reset Logs
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {purgeResult && (
              <span
                className="text-xs text-muted-foreground"
                data-testid="purge-test-signals-result"
              >
                {purgeResult}
              </span>
            )}
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
              <span className="font-medium text-yellow-800">{pendingUsers.length}</span>
              <span className="text-yellow-600">Pending</span>
            </div>
          </div>
        </div>

        {/* Pending Requests Alert */}
        {pendingUsers.length > 0 && (
          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">
                    {pendingUsers.length} user{pendingUsers.length > 1 ? 's' : ''} awaiting approval
                  </p>
                  <p className="text-sm text-yellow-600 mt-1">
                    Review the pending requests below to grant or deny platform access.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feb 2026 — Custom Payment Link generator (admin-only, additive).
            Creates a one-off Stripe Checkout Session for an arbitrary CAD
            amount. Does NOT interact with existing subscription checkout,
            billing gate, or webhooks. */}
        <Card data-testid="custom-payment-link-card">
          <CardContent className="pt-4">
            <button
              type="button"
              onClick={() => setPayLinkOpen(v => !v)}
              className="w-full flex items-center justify-between text-left"
              data-testid="custom-payment-link-toggle"
            >
              <div>
                <p className="font-semibold text-base">Custom Payment Link</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Generate a Stripe Checkout URL with a custom CAD amount, tier, and duration.
                </p>
              </div>
              <span className="text-xl leading-none">{payLinkOpen ? '−' : '+'}</span>
            </button>

            {payLinkOpen && (
              <div className="mt-4 space-y-3 border-t pt-4" data-testid="custom-payment-link-form">
                <div>
                  <label className="text-xs font-medium block mb-1">Customer Email</label>
                  <input
                    type="email"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="customer@example.com"
                    value={payLinkForm.customer_email}
                    onChange={e => setPayLinkForm(f => ({ ...f, customer_email: e.target.value }))}
                    data-testid="paylink-email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium block mb-1">Tier</label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm bg-white"
                      value={payLinkForm.tier}
                      onChange={e => setPayLinkForm(f => ({ ...f, tier: e.target.value }))}
                      data-testid="paylink-tier-select"
                    >
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="elite">Elite</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Duration</label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm bg-white"
                      value={payLinkForm.duration}
                      onChange={e => setPayLinkForm(f => ({ ...f, duration: e.target.value }))}
                      data-testid="paylink-duration-select"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                      <option value="one_time">One-time</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Amount (CAD)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="149.00"
                    value={payLinkForm.amount_cad}
                    onChange={e => setPayLinkForm(f => ({ ...f, amount_cad: e.target.value }))}
                    data-testid="paylink-amount-input"
                  />
                </div>
                <button
                  type="button"
                  className="w-full bg-black text-white text-sm font-medium py-2 rounded disabled:opacity-60"
                  disabled={payLinkGenerating}
                  onClick={handleGeneratePayLink}
                  data-testid="paylink-generate-btn"
                >
                  {payLinkGenerating ? 'Generating…' : 'Generate Link'}
                </button>
                {payLinkError && (
                  <p className="text-xs text-red-600" data-testid="paylink-error">{payLinkError}</p>
                )}
                {payLinkResult?.url && (
                  <div className="pt-2 border-t space-y-2" data-testid="paylink-result">
                    <p className="text-xs font-medium">Checkout URL:</p>
                    <div className="flex items-stretch gap-2">
                      <input
                        type="text"
                        readOnly
                        value={payLinkResult.url}
                        className="flex-1 border rounded px-2 py-1 text-xs font-mono bg-gray-50"
                        onFocus={e => e.target.select()}
                        data-testid="paylink-url-input"
                      />
                      <button
                        type="button"
                        onClick={handleCopyPayLink}
                        className="bg-gray-100 border rounded px-3 text-xs font-medium hover:bg-gray-200"
                        data-testid="paylink-copy-btn"
                      >
                        {payLinkCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      ${(payLinkResult.amount_cents / 100).toFixed(2)} CAD · session {payLinkResult.session_id}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Win-back analytics — admin-only insight strip showing the
            involuntary-churn recovery funnel for the current month.
            Strictly read-only; failures load silently. */}
        <WinbackAnalyticsTile token={token} API={API} />

        {/* Retargeting events — admin visibility panel (read-only) */}
        <RetargetingEventsTile token={token} API={API} />

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-4">
          <TabsList>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              All Users ({users.length})
            </TabsTrigger>
          </TabsList>

          {/* Pending Users Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Pending Access Requests</CardTitle>
                <CardDescription>
                  Users waiting for administrator approval to access the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p className="font-medium">No pending requests</p>
                    <p className="text-sm mt-1">All access requests have been processed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingUsers.map((pendingUser) => (
                      <div 
                        key={pendingUser.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                        data-testid={`pending-user-${pendingUser.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#B89B5E]/10 flex items-center justify-center">
                            <span className="text-lg font-semibold text-[#B89B5E]">
                              {pendingUser.name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div className="space-y-1">
                            <p className="font-medium">{pendingUser.name}</p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5" />
                                {pendingUser.email}
                              </span>
                              {pendingUser.company && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3.5 h-3.5" />
                                  {pendingUser.company}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {formatDate(pendingUser.created_at)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Requested role: <span className="font-medium">{pendingUser.requested_role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Sales Rep'}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#B89B5E]/40 text-[#B89B5E] hover:bg-[#B89B5E]/10"
                            onClick={() => handleCompUser(pendingUser)}
                            data-testid={`comp-user-${pendingUser.id}`}
                            title="Approve & comp trial — admin picks 7/14/30/60/90 days (card required)"
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            Approve & Comp…
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedUser(pendingUser);
                              setShowRejectDialog(true);
                            }}
                            data-testid={`reject-user-${pendingUser.id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => {
                              setSelectedUser(pendingUser);
                              setSelectedRole(pendingUser.requested_role || 'sales_rep');
                              setShowApproveDialog(true);
                            }}
                            data-testid={`approve-user-${pendingUser.id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle>All Users</CardTitle>
                <CardDescription>
                  Complete list of all registered users and their access status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.company || '-'}</TableCell>
                        <TableCell>
                          {u.approval_status === 'approved' || !u.approval_status ? (
                            <Select
                              value={u.role}
                              onValueChange={(value) => handleUpdateRole(u.id, value)}
                              disabled={u.email === user?.email}
                            >
                              <SelectTrigger className="w-[160px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ROLES.map((role) => (
                                  <SelectItem key={role.value} value={role.value}>
                                    {role.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {u.requested_role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {getStatusBadge(u.approval_status || 'approved')}
                            {u.billing_status === 'pending_billing' && (
                              <Badge
                                variant="outline"
                                className="text-[10px] border-orange-300 text-orange-700 bg-orange-50"
                                title="Approved but never completed Stripe checkout — API access blocked until they do."
                                data-testid={`pending-billing-badge-${u.id}`}
                              >
                                Awaiting billing
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {/* Re-send billing setup link — ANY user stuck in pending_billing (OAuth or email/password) */}
                            {u.billing_status === 'pending_billing' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-7 px-2"
                                onClick={() => handleResendBillingSetup(u)}
                                title="Re-send billing setup email (mints a fresh 7-day magic link)"
                                data-testid={`resend-billing-setup-${u.id}`}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Force Activate — fail-safe override: grants permanent
                                access without Stripe. Master-admin-only on the backend. */}
                            {u.email !== user?.email && !u.access_override && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 px-2"
                                onClick={() => handleForceActivate(u)}
                                title="Force activate — grant permanent access without Stripe (master admin only)"
                                data-testid={`force-activate-${u.id}`}
                              >
                                <Shield className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Comp Button - 30-day Professional override (approved users only) */}
                            {(u.approval_status === 'approved' || !u.approval_status) && u.email !== user?.email && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#B89B5E] hover:text-[#B89B5E] hover:bg-[#B89B5E]/10 h-7 px-2"
                                onClick={() => handleCompUser(u)}
                                title="Comp trial — admin picks 7/14/30/60/90 days (card required via Stripe)"
                                data-testid={`comp-user-row-${u.id}`}
                              >
                                <Sparkles className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Reset Password Button - always visible for approved users */}
                            {u.approval_status !== 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-7 px-2"
                                onClick={() => openPasswordResetDialog(u)}
                                title="Reset Password"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                            )}
                            {u.approval_status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowRejectDialog(true);
                                  }}
                                >
                                  <UserX className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 px-2"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setSelectedRole(u.requested_role || 'sales_rep');
                                    setShowApproveDialog(true);
                                  }}
                                >
                                  <UserCheck className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-600" />
                Approve User Access
              </DialogTitle>
              <DialogDescription>
                Grant platform access to {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <p className="font-medium">{selectedUser?.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
                {selectedUser?.company && (
                  <p className="text-sm text-muted-foreground">{selectedUser?.company}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Assign Role</label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-muted-foreground">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleApprove} 
                disabled={processing}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {processing ? 'Approving...' : 'Approve User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <UserX className="w-5 h-5" />
                Reject User Access
              </DialogTitle>
              <DialogDescription>
                Deny platform access to this user
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 space-y-2">
                <p className="font-medium text-red-900">{selectedUser?.name}</p>
                <p className="text-sm text-red-700">{selectedUser?.email}</p>
              </div>
              
              <p className="text-sm text-muted-foreground">
                The user will be notified that their access request has been rejected. 
                They will not be able to log into the platform.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReject} 
                disabled={processing}
                variant="destructive"
              >
                {processing ? 'Rejecting...' : 'Reject User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Comp Grant Dialog (May 11, 2026) — admin picks duration 7/14/30/60/90 */}
        <Dialog open={showCompDialog} onOpenChange={setShowCompDialog}>
          <DialogContent data-testid="comp-grant-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#B89B5E]" />
                Approve & Comp Trial Days
              </DialogTitle>
              <DialogDescription>
                Approve <strong>{compTargetUser?.email}</strong> and grant a comped Stripe trial.
                User must enter a card via <code className="text-xs">/pricing → /signup</code> to activate.
                After the trial ends, Stripe auto-charges at Professional rate unless cancelled.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="rounded-sm border border-[#B89B5E]/30 bg-[#B89B5E]/5 p-3 text-xs space-y-1">
                <p className="font-semibold text-[#8a6a2f]">This is the card-required comp path.</p>
                <p className="text-stone-600">
                  For card-free enterprise access, use <strong>Workspace Settings → Overrides</strong> instead.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="comp-days" className="text-xs uppercase tracking-wider font-semibold text-stone-600">
                  Trial duration
                </label>
                <Select value={compDays} onValueChange={handleCompDaysChange}>
                  <SelectTrigger id="comp-days" data-testid="comp-days-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7" data-testid="comp-days-7">7 days · Premium taste</SelectItem>
                    <SelectItem value="14" data-testid="comp-days-14">14 days · Standard trial</SelectItem>
                    <SelectItem value="30" data-testid="comp-days-30">30 days · Sales-team comp</SelectItem>
                    <SelectItem value="60" data-testid="comp-days-60">60 days · Extended evaluation</SelectItem>
                    <SelectItem value="90" data-testid="comp-days-90">90 days · Enterprise pilot</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="comp-reason" className="text-xs uppercase tracking-wider font-semibold text-stone-600">
                  Reason <span className="font-normal text-stone-400 normal-case">(optional, audit-logged)</span>
                </label>
                <Input
                  id="comp-reason"
                  value={compReason}
                  onChange={(e) => setCompReason(e.target.value)}
                  placeholder="e.g. Q2 partnership pilot"
                  data-testid="comp-reason-input"
                  maxLength={200}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCompDialog(false)}
                disabled={compSubmitting}
                data-testid="comp-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submitCompGrant}
                disabled={compSubmitting}
                className="bg-[#B89B5E] hover:bg-[#A88B4E] text-white"
                data-testid="comp-confirm-btn"
              >
                {compSubmitting ? 'Granting…' : `Approve & Comp ${compDays} Days`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-500" />
                Reset Password
              </DialogTitle>
              <DialogDescription>
                Set a new password for <strong>{passwordResetUser?.email}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  The user will need to use this new password to log in.
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">New Password</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={generateTempPassword}
                    title="Generate temporary password"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Minimum 6 characters</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleResetPassword} 
                disabled={resettingPassword || !newPassword || newPassword.length < 6}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="confirm-reset-password"
              >
                {resettingPassword ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Reset Logs Dialog */}
        <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Password Reset Audit Log
              </DialogTitle>
              <DialogDescription>
                History of password resets performed by administrators
              </DialogDescription>
            </DialogHeader>
            
            <div className="max-h-96 overflow-y-auto">
              {resetLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No password resets recorded</p>
              ) : (
                <div className="space-y-2">
                  {resetLogs.map((log, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p><strong>{log.admin_email}</strong> reset password for <strong>{log.target_user_email}</strong></p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLogsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
